/* eslint-disable array-callback-return */
import React, { useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import cloneDeep from 'lodash/cloneDeep'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { useSetState } from '@campaignhub/react-hooks'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Button, ModalContext, SidebarModal, Box, ListItem,
} from '@campaignhub/suit-theme'

import { digObject } from '@campaignhub/javascript-utils'
import EmailReplacement from './components/EmailReplacement'

const createEmailReplacement = (entityParams, createFn) => {
  createFn(entityParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
    }
  })
}

const deleteEmailReplacement = (entityParams, deleteFn) => {
  deleteFn(entityParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
    }
  })
}

const updateEmailReplacement = (entityParams, updateFn) => {
  updateFn(entityParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
    }
  })
}

const defaultState = {
  replacementList: [],
}

const CreateOrEditEmailReplacementsModal = (props) => {
  const { callbacks, modalKey, showModal } = props

  const [state, setState] = useSetState(defaultState)
  const { replacementList } = state

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, `${modalKey}`, {})
  const {
    callbacks: {
      createEmailReplacement: createFn,
      deleteEmailReplacement: deleteFn,
      updateEmailReplacement: updateFn,
    },
  } = modalPayload

  const { emailReplacements } = useSelector(reduxState => reduxState.entities)
  const { creating, deleting, updating } = useSelector(reduxState => reduxState.emailReplacements)

  useEffect(() => {
    setState({ replacementList: cloneDeep(Object.values(emailReplacements))
      .sort((a, b) => (a.input.toLowerCase() > b.input.toLowerCase() ? 1 : -1))
      .map((replacement, index) => ({
        ...replacement,
        sort: index,
      })),
    })
  }, [emailReplacements])

  const addEmail = () => {
    const newList = replacementList
    const tempId = newList.length ? newList[newList.length - 1].id + 1 : 1

    newList.push({
      id: tempId,
      input: '',
      output: '',
      sort: tempId,
    })

    setState({ replacementList: newList })
  }

  const removeEmail = (emailReplacement) => {
    const newList = replacementList.filter(x => x.id !== emailReplacement.id)

    swal.fire({
      title: 'Delete Email Replacement?',
      text: 'Note that this will also remove unsaved changes. Are you sure you want to proceed? ',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        if (Object.values(emailReplacements).find(x => x.id === emailReplacement.id)){
          deleteEmailReplacement(emailReplacement, deleteFn)
        }
        setState({ replacementList: newList })
      }
    })
  }

  const updateEmail = (emailReplacementId, replaceWith) => {
    const newList = replacementList
    const toUpdate = newList.find(x => x.id === emailReplacementId)
    toUpdate.input = replaceWith.input
    toUpdate.output = replaceWith.output

    setState({ replacementList: newList })
  }

  const saveEmailReplacements = () => {
    const changes = replacementList.filter(x => !Object.values(emailReplacements).some((y) => {
      const { sort, ...temp } = x
      return JSON.stringify(temp) === JSON.stringify(y)
    }))

    changes.map((x) => {
      const { id, ...email } = x
      if (Object.values(emailReplacements).find(y => y.id === x.id)){
        updateEmailReplacement(x, updateFn)
      } else if (email.input.trim() === '' && email.output.trim() === ''){
        // ignore
      } else {
        createEmailReplacement(email, createFn)
      }
    })
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={modalKey} showModal={showModal} size="large">
      <SidebarModal.Header callbacks={callbacks} title="Email" titleSecondLine="Replacement" />
      <SidebarModal.Content>
        <Box flexDirection="column" border="1px solid" borderColor="lineColor" borderRadius={5}>
          {replacementList.sort((a, b) => (a.sort > b.sort ? 1 : -1)).map(emailReplacement => (
            <EmailReplacement
              key={emailReplacement.id}
              callbacks={{
                toggleRemoveEmailReplacement: () => removeEmail(emailReplacement),
                toggleUpdateEmailReplacement: value => updateEmail(emailReplacement.id, value),
              }}
              replacementDetails={emailReplacement}
            />
          ))}
          <ListItem
            boxProps={{
              borderBottom: '1px solid',
              padding: 'large',
            }}
            flexDirection="column"
          >
            <Box marginLeft="auto" width="auto">
              <Button
                buttonStyle="secondaryCreate"
                icon={<FontAwesomeIcon icon={faPlus} />}
                loading={deleting}
                onClick={() => addEmail()}
                size="medium"
                style={{ width: 'auto' }}
              >
                Add Email
              </Button>
            </Box>
          </ListItem>
        </Box>
      </SidebarModal.Content>
      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={closeModal}
          size="medium"
          style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
        >
          Cancel
        </Button>
        <Button
          buttonStyle="primaryCreate"
          loading={creating || deleting || updating}
          onClick={() => saveEmailReplacements()}
          size="medium"
          style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditEmailReplacementsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditEmailReplacementsModal.defaultProps = {
  modalKey: 'CreateOrEditEmailReplacementsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditEmailReplacementsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
